import React from 'react';
import { motion } from 'framer-motion';

const ServicesSection = ({ services }) => {
    if (!services || services.length === 0) return null;

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    return (
        <section className="py-20 md:py-32 bg-agro-cream/30 relative overflow-hidden" id="services">
            {/* Background Accent */}
            <div className="absolute top-0 right-0 w-1/3 h-full bg-agro-lime/5 -skew-x-12 transform translate-x-1/2"></div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-end mb-20">
                    <div className="max-w-2xl">
                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="text-agro-green font-sans uppercase tracking-[0.3em] text-xs mb-4 font-black"
                        >
                            Our Expertise
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl md:text-6xl font-serif text-agro-black leading-tight"
                        >
                            Pioneering Sustainable <br />
                            <span className="text-agro-green">Poultry Solutions</span>
                        </motion.h2>
                    </div>
                </div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10"
                >
                    {services.map((service, index) => (
                        <motion.div
                            key={service.id || index}
                            variants={itemVariants}
                            className="group flex flex-col"
                        >
                            <div className="aspect-[4/5] w-full overflow-hidden rounded-[2rem] mb-8 relative shadow-2xl">
                                <img
                                    src={service.image_url || "https://dummyimage.com/600x800/eee/aaa"}
                                    alt={service.title}
                                    className="w-full h-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-agro-green-dark/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="absolute bottom-6 left-6 right-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                                    <span className="text-white text-xs font-bold uppercase tracking-widest bg-agro-lime/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                                        Learn More
                                    </span>
                                </div>
                            </div>

                            <h3 className="text-2xl font-serif text-agro-black mb-4 group-hover:text-agro-green transition-colors flex items-center gap-3">
                                {service.title}
                                <span className="w-0 h-px bg-agro-green group-hover:w-12 transition-all duration-500"></span>
                            </h3>
                            <p className="text-gray-600 font-sans text-lg leading-relaxed mb-6 line-clamp-3">
                                {service.description}
                            </p>

                            {service.features && service.features.length > 0 && (
                                <ul className="text-xs text-gray-400 space-y-2 border-t border-gray-100 pt-6 mt-auto">
                                    {service.features.slice(0, 3).map((f, i) => (
                                        <li key={i} className="flex items-center group/item hover:text-agro-green transition-colors">
                                            <span className="w-1.5 h-1.5 bg-agro-lime rounded-full mr-3 transform group-hover/item:scale-150 transition-transform" />
                                            <span className="font-medium tracking-wide uppercase">{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ServicesSection;
